import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useMemo, memo, useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";

import classes from './WorkQComponent.module.css'
import OverflowContainer from "../../fragments/OverflowContainer";
import Pagination from "./pagination/Pagination";
import WorkQHeader from "./WorkQHeader";
import WorkQRow from "./WorkQRow";
import { retrieveSpecificWorkQ } from "../../../lib/systemData";
import CustomWorkQs from "./CustomWorkQs";
import WorkQComponentLoading from "./custom/WorkQComponentLoading";
import { WorkQShellProvider } from "../context/workQShellContext";
import { customWorkQSlugs } from "./custom/globalWorkQStructure";
import { socket } from "../../../socket";
import { workqueueActions } from "../../../store/workqueue";
import { filterActions } from "../../../store/filter";
import { computeStickyColumnOffsets } from "../../../lib/workQStickyLayout";
import { getWorkQGroupedFields, getWorkQGroupedFieldsForUser } from "../../../lib/workQ";
import { deletedRecordIds, omitDeletedRecords, removeDeletedIds, withoutDeletedQueueFields } from "../../../lib/noteDelete";
import { applyDeletedNotesPatch, dispatchDeletedTasksRemoved } from "../../../lib/applyNoteSoftDelete";


/** Must match progressive path in `displayedRows` and the render-limit effect. */
const WORKQ_PROGRESSIVE_INITIAL = 30;
const PAYER_SOCKET_GUARD_TTL_MS = 12000;

/**
 * Mount at most this many rows in the first paint when using the "meta page" cap.
 * Must be ≥ common per_page options (e.g. 50) so those pages skip tbody virtualization —
 * otherwise hover/edit waits on transition-scheduled row commits (user-reported 3–4s lag).
 * 100+ row pages still virtualize / chunk.
 */
const WORKQ_SYNC_MOUNT_ROW_MAX = 72;

function workqShouldPaintAllRowsAtOnce(total, perPage) {
    if (!total) return true;
    const fastPathCap = Math.max(perPage || 0, 120);
    return total <= fastPathCap && total <= WORKQ_SYNC_MOUNT_ROW_MAX;
}

/** Large pages: mount only visible <tr> nodes; sockets still use full page list. */
const WORKQ_TABLE_VIRTUALIZE_LARGE_PAGES = true;
/** Taller estimate + overscan reduce virtual window edge flapping (logs showed end 30↔31). */
const WORKQ_VIRTUAL_ROW_ESTIMATE_PX = 58;
const WORKQ_VIRTUAL_OVERSCAN = 16;

function computeWorkqVirtualWindow(rowCount, scrollTop, viewportH) {
    if (!rowCount) return { start: 0, end: 0, padTop: 0, padBot: 0 };
    const est = WORKQ_VIRTUAL_ROW_ESTIMATE_PX;
    const os = WORKQ_VIRTUAL_OVERSCAN;
    const H = viewportH || 720;
    const st = Number(scrollTop) || 0;
    const start = Math.max(0, Math.floor(st / est) - os);
    const end = Math.min(rowCount, Math.ceil((st + H) / est) + os);
    return {
        start,
        end,
        padTop: start * est,
        padBot: Math.max(0, rowCount - end) * est,
    };
}

function workqFieldVisibleInTable(item) {
    if (item?.show_in_table === undefined || item?.show_in_table === null) return true;
    return item.show_in_table === 1 || item.show_in_table === true || item.show_in_table === "1";
}

function countVisibleWorkQDataColumns(workq) {
    const grouped = getWorkQGroupedFields(workq);
    if (!grouped.length) return 1;
    let count = 0;
    for (const item of grouped) {
        if (!item?.items) continue;
        const cells = item.items
            .filter((elem) => elem.type !== "hover" && workqFieldVisibleInTable(elem));
        count += cells.length;
    }
    return Math.max(1, count);
}

function workqPageUsesVirtualTable(total, perPage) {
    return WORKQ_TABLE_VIRTUALIZE_LARGE_PAGES && total > 0 && !workqShouldPaintAllRowsAtOnce(total, perPage);
}

const WQ_STICKY_CSS_PREFIX = "--wq-sticky-";

function clearWorkQStickyCssVars(tableEl, keysRef) {
    if (!tableEl || !Array.isArray(keysRef.current) || keysRef.current.length === 0) return;
    keysRef.current.forEach((key) => {
        tableEl.style.removeProperty(`${WQ_STICKY_CSS_PREFIX}${key}`);
    });
    keysRef.current = [];
}

function applyWorkQStickyCssVars(tableEl, offsets, keysRef) {
    if (!tableEl) return;
    const list = Array.isArray(offsets) ? offsets : [];
    const nextKeys = new Set(list.map((o) => o?.index).filter(Boolean));
    (keysRef.current || []).forEach((key) => {
        if (!nextKeys.has(key)) {
            tableEl.style.removeProperty(`${WQ_STICKY_CSS_PREFIX}${key}`);
        }
    });
    list.forEach((o) => {
        if (o?.index != null && typeof o.left === "number") {
            tableEl.style.setProperty(`${WQ_STICKY_CSS_PREFIX}${o.index}`, `${o.left}px`);
        }
    });
    keysRef.current = [...nextKeys];
}


// Memoize the Pagination component
const MemoizedPagination = memo(Pagination);

function getCaseIdFromRow(row) {
    return row?.case?.id ?? row?.case_id ?? null;
}

/**
 * True when every own value is null/undefined/empty string — typical of a WorkQueue
 * resource group when its Eloquent relation was not eager-loaded for the socket emit.
 */
function isEmptyObjectStub(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const values = Object.values(value);
    if (values.length === 0) {
        return true;
    }
    return values.every((entry) => entry == null || entry === "");
}

/**
 * Deep-merge one nested WorkQ folder (e.g. work_queue_gdq_mrq).
 * Keeps the current folder when the socket payload is an all-null stub so fields
 * like mrq_notes are not wiped by EmitWorkQData when a relation was missing.
 */
function mergeNestedWorkQFolder(currentFolder, incomingFolder) {
    if (incomingFolder == null) {
        return currentFolder ?? incomingFolder;
    }
    if (typeof incomingFolder !== "object" || Array.isArray(incomingFolder)) {
        return incomingFolder;
    }
    if (!currentFolder || typeof currentFolder !== "object" || Array.isArray(currentFolder)) {
        return incomingFolder;
    }
    if (isEmptyObjectStub(incomingFolder) && !isEmptyObjectStub(currentFolder)) {
        return currentFolder;
    }
    return {
        ...currentFolder,
        ...incomingFolder,
    };
}

function mergeIncomingRowData(currentRow, incomingData, caseId) {
    if (!incomingData || typeof incomingData !== "object") {
        return currentRow;
    }

    const currentCaseUpdatedAt = currentRow?.case?.updated_at ?? currentRow?.updated_at ?? null;
    const incomingCaseUpdatedAt =
        incomingData?.case?.updated_at ?? incomingData?.updated_at ?? null;
    const currentCaseUpdatedAtMs = currentCaseUpdatedAt ? new Date(currentCaseUpdatedAt).getTime() : NaN;
    const incomingCaseUpdatedAtMs = incomingCaseUpdatedAt ? new Date(incomingCaseUpdatedAt).getTime() : NaN;
    const hasComparableCaseTimestamps =
        Number.isFinite(currentCaseUpdatedAtMs) && Number.isFinite(incomingCaseUpdatedAtMs);
    const shouldApplyIncomingCase =
        !hasComparableCaseTimestamps || incomingCaseUpdatedAtMs >= currentCaseUpdatedAtMs;

    const { task_notes, notes, work_queue_table_field, ...safeIncomingData } = incomingData;
    const safeIncomingCase = safeIncomingData?.case;
    const { case: _incomingCaseIgnored, ...incomingRowFields } = safeIncomingData;

    const mergedRow = {
        ...currentRow,
    };

    Object.keys(incomingRowFields).forEach((key) => {
        const incomingValue = incomingRowFields[key];
        const currentValue = currentRow?.[key];
        const bothArePlainObjects =
            incomingValue &&
            typeof incomingValue === "object" &&
            !Array.isArray(incomingValue) &&
            currentValue &&
            typeof currentValue === "object" &&
            !Array.isArray(currentValue);

        mergedRow[key] = bothArePlainObjects
            ? mergeNestedWorkQFolder(currentValue, incomingValue)
            : incomingValue;
    });

    mergedRow.case = {
        ...currentRow?.case,
        ...(shouldApplyIncomingCase ? safeIncomingCase : {}),
        id: caseId,
    };

    return mergedRow;
}

function mergeNotesTasksTableField(currentRow, incomingData) {
    if (!incomingData || typeof incomingData !== "object") {
        return currentRow;
    }

    const sanitized = withoutDeletedQueueFields(incomingData);
    const nextRow = { ...currentRow };

    if (Object.prototype.hasOwnProperty.call(incomingData, "notes")) {
        nextRow.notes = sanitized.notes;
    }

    if (Object.prototype.hasOwnProperty.call(incomingData, "task_notes")) {
        nextRow.task_notes = sanitized.task_notes;
    }

    if (Object.prototype.hasOwnProperty.call(incomingData, "tasks")) {
        nextRow.tasks = sanitized.tasks;
    }

    if (Object.prototype.hasOwnProperty.call(incomingData, "work_queue_table_field")) {
        nextRow.work_queue_table_field = incomingData.work_queue_table_field;
    }

    return nextRow;
}

function replaceNotesIfNoNoteTypes(currentRow, incomingData) {
    if (!incomingData || typeof incomingData !== "object") {
        return currentRow;
    }

    return {
        ...currentRow,
        notes: omitDeletedRecords(incomingData.notes),
    };
}

function dropDeletedFromRow(row, incomingData) {
    const dropIds = new Set(
        [
            ...deletedRecordIds(incomingData?.notes),
            ...deletedRecordIds(incomingData?.task_notes),
            ...deletedRecordIds(incomingData?.tasks),
        ].map((id) => String(id))
    );
    if (dropIds.size === 0) {
        return row;
    }
    return {
        ...row,
        notes: removeDeletedIds(row?.notes, dropIds),
        task_notes: removeDeletedIds(row?.task_notes, dropIds),
        tasks: removeDeletedIds(row?.tasks, dropIds),
    };
}

export default function WorkQComponent() {
    const dispatch = useDispatch()
    let { workqSlug } = useParams(); 
    const location = useLocation()

    if (!workqSlug) {
        customWorkQSlugs.forEach(item => {
            if (location.pathname.includes(item)) {
                workqSlug = item
            }
        })
    }
      
    const patientsList = useSelector(state => state.filter.currentpagedata);
    const storeWorkQSlug = useSelector(state => state.filter.workqSlug);
    const masterLoading = useSelector((state) => state.filter.loading);
    const editPermissions = useSelector((state) => state.permissions.editPermissions);
    const listLen = patientsList?.data?.length || 0;
    const showTableLoadingOverlay = masterLoading && listLen > 0;
    const patientsData = patientsList?.data
    const [renderedRows, setRenderedRows] = useState([]);
    const [renderLimit, setRenderLimit] = useState(0)
    const socketSubscriptionsRef = useRef({});
    const activeWorkqEventIdRef = useRef(null);
    const lastSocketEmittedAtRef = useRef({});
    const payerSocketGuardRef = useRef({});
    const tableFieldOverridesRef = useRef({});
    const tableStickyCssRef = useRef(null);
    const stickyCssVarKeysRef = useRef([]);
    /** Latest row count / per_page for chunk pump callbacks (avoid stale closures). */
    const workqRowTotalRef = useRef(0);
    const workqPerPageRef = useRef(undefined);
    /** Bump on pump effect cleanup so in-flight rAF no-ops without cancelAnimationFrame (cancel was starving chunks). */
    const chunkPumpGenerationRef = useRef(0);
    /** Cancel in-flight batched `socket.on` registration when the workq row set changes. */
    const socketSubscribeBatchGenRef = useRef(0);
    const notesAllSubscribeBatchGenRef = useRef(0);
    /** Skip duplicate chunk bootstraps when meta/`renderLimit` re-runs for the same page. */
    const chunkBootstrapPageKeyRef = useRef("");
    /** Incremental `work_queue_notes_all_*` listeners — avoid full teardown/rebind on every chunk step. */
    const notesAllSubsRef = useRef({});
    const workqScrollRef = useRef(null);
    const workqScrollRafRef = useRef(0);
    /** Scroll position for virtual math — not React state (avoids re-render every scroll frame). */
    const workqScrollTopRef = useRef(0);
    const workqViewportHRef = useRef(720);
    const lastVirtualRangeRef = useRef({ start: -1, end: -1 });
    const [workqViewportH, setWorkqViewportH] = useState(720);
    /** Only updates when visible row range changes — keeps scroll compositor-smooth. */
    const [virtualWindow, setVirtualWindow] = useState(() => ({
        start: 0,
        end: 0,
        padTop: 0,
        padBot: 0,
    }));

    function isStaleSocketEvent(caseIdKey, payload) {
        const emittedAtRaw = payload?.emittedAt ?? payload?.data?.emittedAt ?? payload?.row?.emittedAt;
        if (!emittedAtRaw) return false;
        const emittedAtMs = new Date(emittedAtRaw).getTime();
        if (Number.isNaN(emittedAtMs)) return false;

        const lastMs = lastSocketEmittedAtRef.current?.[caseIdKey];
        if (lastMs != null && emittedAtMs < lastMs) {
            return true;
        }
        lastSocketEmittedAtRef.current[caseIdKey] = emittedAtMs;
        return false;
    }

    function applyTableFieldOverride(caseIdKey, row) {
        const override = tableFieldOverridesRef.current?.[caseIdKey];
        if (!override || typeof override !== "object" || Array.isArray(override)) {
            return row;
        }
        return {
            ...row,
            work_queue_table_field: {
                ...(row?.work_queue_table_field || {}),
                ...override,
            },
        };
    }

    // Memoize the default slug value
    // const defaultSlug = useMemo(() => workqSlug || 'police-report', [workqSlug]);
    const defaultSlug = useMemo(() => workqSlug, [workqSlug]);
    
    // Memoize the workq retrieval
    const workq = useMemo(() => {
        return retrieveSpecificWorkQ(defaultSlug);
    }, [defaultSlug]);
    const emptyNoteTypes = Array.isArray(workq?.note_types) && workq.note_types.length === 0;

    const isCustomWorkQ = workq?.type === "custom";
    const workqEventId = useMemo(() => {
        return workq?.id ?? workq?.slug ?? workqSlug;
    }, [workq, workqSlug]);

    useEffect(() => {
        // Hard reset row/virtual state when switching queues to avoid stale
        // virtual padding or cached rows from the previous WorkQ.
        setRenderedRows([]);
        setRenderLimit(0);
        setVirtualWindow({
            start: 0,
            end: 0,
            padTop: 0,
            padBot: 0,
        });
        chunkBootstrapPageKeyRef.current = "";
        workqScrollTopRef.current = 0;
        lastVirtualRangeRef.current = { start: -1, end: -1 };
        const el = workqScrollRef.current;
        if (el) {
            el.scrollTop = 0;
        }
    }, [defaultSlug]);

    useEffect(() => {
        dispatch(workqueueActions.setOpenedDrawer(null))
    }, [dispatch, workqSlug])

    useEffect(() => {
        // Page data stays on the previous queue until its fetch finishes. Invalid Fax rows
        // have no `case`, so adopting them here makes the next queue throw on `case.id`.
        const nextRows = storeWorkQSlug == workqSlug ? (patientsData || []) : [];

        // Don't clobber local socket updates (e.g. follow-up date table fields)
        // when Redux refreshes the current page data.
        setRenderedRows((prevRows) => {
            if (!Array.isArray(prevRows) || prevRows.length === 0) {
                return nextRows;
            }
            if (!Array.isArray(nextRows) || nextRows.length === 0) {
                return nextRows;
            }

            const prevByCaseId = new Map(
                prevRows
                    .map((row) => [String(getCaseIdFromRow(row) ?? ""), row])
                    .filter(([id]) => id),
            );

            return nextRows.map((nextRow) => {
                const caseIdKey = String(getCaseIdFromRow(nextRow) ?? "");
                const prevRow = prevByCaseId.get(caseIdKey);
                if (!prevRow) {
                    return applyTableFieldOverride(caseIdKey, nextRow);
                }

                // Preserve socket-updated work queue table field values until API catches up.
                const prevTableField = prevRow?.work_queue_table_field;
                const nextTableField = nextRow?.work_queue_table_field;
                const mergedTableField =
                    prevTableField && typeof prevTableField === "object" && !Array.isArray(prevTableField)
                        ? {
                              ...prevTableField,
                              ...(nextTableField && typeof nextTableField === "object" && !Array.isArray(nextTableField)
                                  ? nextTableField
                                  : {}),
                          }
                        : nextTableField;

                const mergedRow = mergedTableField
                    ? { ...nextRow, work_queue_table_field: mergedTableField }
                    : nextRow;
                if (
                    prevRow?.case?.case_payer_type_id !== mergedRow?.case?.case_payer_type_id ||
                    prevRow?.case?.case_payer_status_id !== mergedRow?.case?.case_payer_status_id
                ) {
                    payerSocketGuardRef.current[caseIdKey] = {
                        previousPayerType: prevRow?.case?.case_payer_type_id ?? null,
                        previousPayerStatus: prevRow?.case?.case_payer_status_id ?? null,
                        payerType: mergedRow?.case?.case_payer_type_id ?? null,
                        payerStatus: mergedRow?.case?.case_payer_status_id ?? null,
                        expiresAt: Date.now() + PAYER_SOCKET_GUARD_TTL_MS,
                    };
                }

                // Also re-apply in-memory overrides so Redux refreshes never flash old values.
                return applyTableFieldOverride(caseIdKey, mergedRow);
            });
        });
    }, [patientsData, storeWorkQSlug, workqSlug]);

    // Redux `patientsData` updates in render; `renderedRows` syncs in an effect (after paint). Without this,
    // `patientsList?.data` can be truthy while `renderedRows` is still empty → empty <tbody> until effects run.
    // When both exist but the first row differs (nav / pagination), prefer Redux until local state catches up.
    const rowsForDisplay = useMemo(() => {
        const pageDataMatchesRoute = storeWorkQSlug == workqSlug;
        const pd = pageDataMatchesRoute && Array.isArray(patientsData) ? patientsData : [];
        const rr = pageDataMatchesRoute && Array.isArray(renderedRows) ? renderedRows : [];
        if (pd.length === 0 && rr.length === 0) return [];
        if (rr.length === 0) return pd;
        if (pd.length === 0) return rr;
        const pdFirst = String(getCaseIdFromRow(pd[0]) ?? "");
        const rrFirst = String(getCaseIdFromRow(rr[0]) ?? "");
        if (pdFirst && rrFirst && pdFirst !== rrFirst) return pd;
        return rr;
    }, [renderedRows, patientsData, storeWorkQSlug, workqSlug]);

    /** Disambiguate refetches with identical meta/total so chunk bootstrap cannot skip on stale `renderLimit`. */
    const chunkBootstrapDataSig = useMemo(() => {
        const row = Array.isArray(patientsData) && patientsData.length > 0 ? patientsData[0] : null;
        return row ? String(getCaseIdFromRow(row) ?? "") : "";
    }, [patientsData]);

    workqRowTotalRef.current = rowsForDisplay.length;
    workqPerPageRef.current = patientsList?.meta?.per_page;
    workqViewportHRef.current = workqViewportH;

    // Row mount cap: small pages paint all rows; larger pages bootstrap INITIAL rows then chain below.
    useEffect(() => {
        const total = rowsForDisplay?.length || 0;
        const pageKey = `${defaultSlug}|${patientsList?.meta?.current_page ?? ""}|${patientsList?.meta?.per_page ?? ""}|${total}|${chunkBootstrapDataSig}`;
        if (!total) {
            setRenderLimit(0);
            chunkBootstrapPageKeyRef.current = "";
            return;
        }

        const perPage = patientsList?.meta?.per_page;
        if (workqShouldPaintAllRowsAtOnce(total, perPage)) {
            chunkBootstrapPageKeyRef.current = pageKey;
            setRenderLimit(total);
            return;
        }

        if (workqPageUsesVirtualTable(total, perPage)) {
            if (chunkBootstrapPageKeyRef.current === pageKey && renderLimit === total) {
                return;
            }
            chunkBootstrapPageKeyRef.current = pageKey;
            setRenderLimit(total);
            return;
        }

        if (chunkBootstrapPageKeyRef.current === pageKey && renderLimit > 0) {
            return;
        }
        chunkBootstrapPageKeyRef.current = pageKey;
        const initial = Math.min(WORKQ_PROGRESSIVE_INITIAL, total);
        setRenderLimit(initial);
        // Primitives only: `rowsForDisplay` gets a new array reference on many Redux/socket updates; re-running
        // this effect would reset `renderLimit` and cancel the rAF pump below (multi-second gaps between chunks).
    }, [
        rowsForDisplay.length,
        patientsList?.meta?.per_page,
        patientsList?.meta?.current_page,
        defaultSlug,
        renderLimit,
        chunkBootstrapDataSig,
    ]);

    /** Legacy progressive mount when virtualization is off (should not run for 100-row pages). */
    const WORKQ_CHUNK_STEP = 14;
    useEffect(() => {
        const total = workqRowTotalRef.current;
        const perPage = workqPerPageRef.current;
        if (!total || workqShouldPaintAllRowsAtOnce(total, perPage)) return;
        if (workqPageUsesVirtualTable(total, perPage)) return;
        if (renderLimit >= total) return;
        if (renderLimit === 0) return;

        const gen = ++chunkPumpGenerationRef.current;
        requestAnimationFrame(() => {
            if (chunkPumpGenerationRef.current !== gen) return;
            requestAnimationFrame(() => {
                if (chunkPumpGenerationRef.current !== gen) return;
                setRenderLimit((prev) => {
                    const cap = workqRowTotalRef.current;
                    const pp = workqPerPageRef.current;
                    if (!cap || workqShouldPaintAllRowsAtOnce(cap, pp)) return prev;
                    if (workqPageUsesVirtualTable(cap, pp)) return prev;
                    return Math.min(cap, prev + WORKQ_CHUNK_STEP);
                });
            });
        });
        return () => {
            chunkPumpGenerationRef.current += 1;
        };
    }, [
        renderLimit,
        rowsForDisplay.length,
        patientsList?.meta?.per_page,
        patientsList?.meta?.current_page,
        defaultSlug,
    ]);

    const displayedRows = useMemo(() => {
        if (!rowsForDisplay?.length) return [];
        const total = rowsForDisplay.length;
        const perPage = patientsList?.meta?.per_page;
        const metaTotal = Number(patientsList?.meta?.total) || 0;
        const normalizedPerPage = Number(perPage) || 0;
        // When per_page is reduced (e.g. 50 -> 20), meta can update before new data arrives.
        // Clamp stale rows immediately so UI reflects new page size without waiting for API payload.
        if (
            normalizedPerPage > 0 &&
            metaTotal >= normalizedPerPage &&
            total > normalizedPerPage
        ) {
            return rowsForDisplay.slice(0, normalizedPerPage);
        }
        if (workqShouldPaintAllRowsAtOnce(total, perPage)) {
            return rowsForDisplay;
        }
        if (workqPageUsesVirtualTable(total, perPage)) {
            return rowsForDisplay;
        }
        const effective =
            renderLimit > 0 ? renderLimit : Math.min(WORKQ_PROGRESSIVE_INITIAL, total);
        const limit = Math.max(0, Math.min(total, effective));
        return rowsForDisplay.slice(0, limit);
    }, [rowsForDisplay, renderLimit, patientsList?.meta?.per_page, patientsList?.meta?.total]);

    const renderedCaseIds = useMemo(() => {
        return (displayedRows || [])
            .map((row) => getCaseIdFromRow(row))
            .filter(Boolean);
    }, [displayedRows]);

    const displayedRowIdsSignature = useMemo(() => {
        return (displayedRows || [])
            .map((row) => getCaseIdFromRow(row))
            .filter(Boolean)
            .join(",");
    }, [displayedRows]);

    const usesVirtualTableBody = useMemo(() => {
        const total = displayedRows.length;
        const perPage = patientsList?.meta?.per_page;
        return workqPageUsesVirtualTable(total, perPage);
    }, [displayedRows.length, patientsList?.meta?.per_page]);

    const normalizedGroupedFields = useMemo(
        () => getWorkQGroupedFieldsForUser(workq, editPermissions),
        [workq?.grouped_fields, editPermissions],
    );

    const virtualTableColCount = useMemo(() => {
        if (!normalizedGroupedFields.length) return 1;
        let count = 0;
        for (const item of normalizedGroupedFields) {
            if (!item?.items) continue;
            const cells = item.items.filter(
                (elem) => elem.type !== "hover" && workqFieldVisibleInTable(elem),
            );
            count += cells.length;
        }
        return Math.max(1, count);
    }, [normalizedGroupedFields]);

    useLayoutEffect(() => {
        const el = workqScrollRef.current;
        if (el) {
            el.scrollTop = 0;
        }
        workqScrollTopRef.current = 0;
        lastVirtualRangeRef.current = { start: -1, end: -1 };
        if (workqScrollRafRef.current) {
            cancelAnimationFrame(workqScrollRafRef.current);
            workqScrollRafRef.current = 0;
        }
    }, [defaultSlug, patientsList?.meta?.current_page, patientsList?.meta?.per_page, chunkBootstrapDataSig]);

    useLayoutEffect(() => {
        if (!usesVirtualTableBody || !displayedRows.length) {
            return;
        }
        const n = displayedRows.length;
        const el = workqScrollRef.current;
        let st = el?.scrollTop ?? workqScrollTopRef.current ?? 0;
        const maxScroll = el ? Math.max(0, el.scrollHeight - el.clientHeight) : 0;
        if (el && maxScroll > 0 && st > maxScroll) {
            st = maxScroll;
            el.scrollTop = st;
        }
        workqScrollTopRef.current = st;
        const vw = computeWorkqVirtualWindow(n, st, workqViewportH);
        lastVirtualRangeRef.current = { start: vw.start, end: vw.end };
        setVirtualWindow(vw);
    }, [
        usesVirtualTableBody,
        displayedRows.length,
        workqViewportH,
        chunkBootstrapDataSig,
        defaultSlug,
        patientsList?.meta?.current_page,
        patientsList?.meta?.per_page,
    ]);

    const onWorkqTableScroll = useCallback(() => {
        const el = workqScrollRef.current;
        if (!el) return;
        const n = workqRowTotalRef.current;
        const perPage = workqPerPageRef.current;
        if (!workqPageUsesVirtualTable(n, perPage)) return;

        workqScrollTopRef.current = el.scrollTop;
        if (workqScrollRafRef.current) return;
        workqScrollRafRef.current = requestAnimationFrame(() => {
            workqScrollRafRef.current = 0;
            const n2 = workqRowTotalRef.current;
            const pp = workqPerPageRef.current;
            if (!workqPageUsesVirtualTable(n2, pp) || !n2) return;
            const st = workqScrollTopRef.current;
            const H = workqViewportHRef.current || 720;
            let vw = computeWorkqVirtualWindow(n2, st, H);
            const prev = lastVirtualRangeRef.current;
            const est = WORKQ_VIRTUAL_ROW_ESTIMATE_PX;
            if (prev.start >= 0 && vw.start === prev.start && Math.abs(vw.end - prev.end) === 1) {
                const end = prev.end;
                vw = {
                    start: vw.start,
                    end,
                    padTop: vw.start * est,
                    padBot: Math.max(0, n2 - end) * est,
                };
            }
            if (prev.start === vw.start && prev.end === vw.end) return;
            lastVirtualRangeRef.current = { start: vw.start, end: vw.end };
            setVirtualWindow(vw);
        });
    }, []);

    useEffect(() => {
        const el = workqScrollRef.current;
        if (!el) return;
        let rafId = 0;
        const applyH = () => {
            rafId = 0;
            setWorkqViewportH(el.clientHeight || 720);
        };
        applyH();
        if (typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => {
            if (rafId) return;
            rafId = requestAnimationFrame(applyH);
        });
        ro.observe(el);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [defaultSlug, patientsList?.meta?.current_page, patientsList?.meta?.per_page, chunkBootstrapDataSig, patientsList?.data]);

    // Sticky column `left` is applied as CSS variables on <table> so rows do not re-render on measure updates.
    useEffect(() => {
        const tableSnapshot = tableStickyCssRef.current;
        const groupedFields = normalizedGroupedFields;
        if (!groupedFields.length || !displayedRows?.length) {
            if (tableSnapshot) clearWorkQStickyCssVars(tableSnapshot, stickyCssVarKeysRef);
            return;
        }
        const rafId = requestAnimationFrame(() => {
            const nextOffsets = computeStickyColumnOffsets(groupedFields);
            const table = tableStickyCssRef.current ?? tableSnapshot;
            applyWorkQStickyCssVars(table, nextOffsets, stickyCssVarKeysRef);
        });
        return () => {
            cancelAnimationFrame(rafId);
            if (tableSnapshot) clearWorkQStickyCssVars(tableSnapshot, stickyCssVarKeysRef);
        };
    }, [normalizedGroupedFields, displayedRowIdsSignature, displayedRows?.length, defaultSlug]);

    // Socket strategy:
    // - subscribe per rendered case via `work_queue_updated-{workqID}-{caseID}`
    // - keep subscriptions incremental (remove stale, add new)
    // - skip socket subscriptions entirely for custom work queues
    // `displayedRowIdsSignature` (not `renderedCaseIds`) keeps this effect from re-running on array identity churn.
    useEffect(() => {
        const desiredCaseIds = new Set(renderedCaseIds.map((id) => String(id)));
        const hasWorkqChanged = activeWorkqEventIdRef.current !== workqEventId;

        // WorkQ changed -> clear old namespace listeners.
        if (hasWorkqChanged) {
            Object.values(socketSubscriptionsRef.current).forEach(
                ({ updatedEventName, updatedEventNames, updatedHandler, notesEventName, notesHandler, followupEventName, followupHandler }) => {
                const updateEvents = Array.isArray(updatedEventNames)
                    ? updatedEventNames
                    : (updatedEventName ? [updatedEventName] : []);
                updateEvents.forEach((eventName) => {
                    socket.off(eventName, updatedHandler);
                });
                socket.off(notesEventName, notesHandler);
                if (followupEventName && followupHandler) {
                    socket.off(followupEventName, followupHandler);
                }
            });
            socketSubscriptionsRef.current = {};
            activeWorkqEventIdRef.current = workqEventId ?? null;
        }

        // Custom queues should never subscribe to work queue socket updates.
        if (isCustomWorkQ || !workqEventId) {
            return;
        }

        if (!socket.connected && desiredCaseIds.size > 0) {
            socket.connect();
        }

        // Remove listeners for cases no longer rendered.
        Object.keys(socketSubscriptionsRef.current).forEach((caseIdKey) => {
            if (!desiredCaseIds.has(caseIdKey)) {
                const subscription = socketSubscriptionsRef.current[caseIdKey];
                const updateEvents = Array.isArray(subscription.updatedEventNames)
                    ? subscription.updatedEventNames
                    : (subscription.updatedEventName ? [subscription.updatedEventName] : []);
                updateEvents.forEach((eventName) => {
                    socket.off(eventName, subscription.updatedHandler);
                });
                socket.off(subscription.notesEventName, subscription.notesHandler);
                if (subscription.followupEventName && subscription.followupHandler) {
                    socket.off(subscription.followupEventName, subscription.followupHandler);
                }
                delete socketSubscriptionsRef.current[caseIdKey];
                delete lastSocketEmittedAtRef.current[caseIdKey];
                delete payerSocketGuardRef.current[caseIdKey];
                delete tableFieldOverridesRef.current[caseIdKey];
            }
        });

        // Add listeners only for newly rendered cases (batched across frames on large pages).
        /** Sync one batch for typical 100/page — logs showed chunk=8 async ~8.7s vs ~5.7s with larger chunks (setTimeout tax). */
        const WORKQ_SOCKET_SUBSCRIBE_SYNC_MAX = 120;
        const WORKQ_SOCKET_SUBSCRIBE_CHUNK = 18;
        const caseIdsToAdd = renderedCaseIds.filter(
            (caseId) => !socketSubscriptionsRef.current[String(caseId)],
        );

        function subscribeWorkqCase(caseId) {
            const caseIdKey = String(caseId);
            if (socketSubscriptionsRef.current[caseIdKey]) return;

            const publishReplacedNoteLists = (incomingData) => {
                if (!incomingData || typeof incomingData !== "object") {
                    return incomingData;
                }
                const sanitized = withoutDeletedQueueFields(incomingData);
                const patch = {};
                if (Object.prototype.hasOwnProperty.call(incomingData, "notes")) {
                    patch.notes = sanitized.notes;
                }
                if (Object.prototype.hasOwnProperty.call(incomingData, "task_notes")) {
                    patch.task_notes = sanitized.task_notes;
                }
                if (Object.prototype.hasOwnProperty.call(incomingData, "tasks")) {
                    patch.tasks = sanitized.tasks;
                }
                if (Object.keys(patch).length) {
                    dispatch(filterActions.batchUpdate({ caseId, data: patch }));
                }
                dispatchDeletedTasksRemoved(dispatch, [
                    ...(Array.isArray(incomingData.notes) ? incomingData.notes : []),
                    ...(Array.isArray(incomingData.task_notes) ? incomingData.task_notes : []),
                    ...(Array.isArray(incomingData.tasks) ? incomingData.tasks : []),
                ]);
                return sanitized;
            };

            const updatedEventNames = [
                `work_queue_updated-${workqEventId}-${caseId}`,
                `work_queue_updated-${caseId}`,
            ];
            const updatedHandler = (payload) => {
                if (isStaleSocketEvent(caseIdKey, payload)) {
                    return;
                }

                const incomingData = payload?.data ?? payload?.row ?? payload;
                applyDeletedNotesPatch(dispatch, [
                    ...(Array.isArray(incomingData?.notes) ? incomingData.notes : []),
                    ...(Array.isArray(incomingData?.task_notes) ? incomingData.task_notes : []),
                    ...(Array.isArray(incomingData?.tasks) ? incomingData.tasks : []),
                ], caseId);
                setRenderedRows((prevRows) =>
                    prevRows.map((row) => {
                        const rowCaseId = getCaseIdFromRow(row);
                        if (String(rowCaseId) !== caseIdKey) return row;
                        const guardEntry = payerSocketGuardRef.current?.[caseIdKey];
                        const isGuardActive = !!(guardEntry && guardEntry.expiresAt > Date.now());
                        const incomingCaseUpdatedAt =
                            incomingData?.case?.updated_at ?? incomingData?.updated_at ?? null;
                        const incomingPayerType =
                            incomingData?.case?.case_payer_type_id ?? incomingData?.case_payer_type_id ?? null;
                        const incomingPayerStatus =
                            incomingData?.case?.case_payer_status_id ?? incomingData?.case_payer_status_id ?? null;
                        const shouldBlockAmbiguousPayerRebound =
                            isGuardActive &&
                            !incomingCaseUpdatedAt &&
                            (incomingPayerType === guardEntry.previousPayerType ||
                                incomingPayerStatus === guardEntry.previousPayerStatus) &&
                            (incomingPayerType !== guardEntry.payerType ||
                                incomingPayerStatus !== guardEntry.payerStatus);

                        if (shouldBlockAmbiguousPayerRebound) {
                            return row;
                        }
                        const merged = mergeIncomingRowData(row, incomingData, rowCaseId);
                        return applyTableFieldOverride(caseIdKey, dropDeletedFromRow(merged, incomingData));
                    }),
                );
            };

            const notesEventName = `work_queue_notes-${workqEventId}-${caseId}`;
            const notesHandler = (payload) => {
                const incomingData = publishReplacedNoteLists(payload?.data ?? payload?.row ?? payload);

                setRenderedRows((prevRows) =>
                    prevRows.map((row) => {
                        const rowCaseId = getCaseIdFromRow(row);
                        if (String(rowCaseId) !== caseIdKey) return row;
                        const merged = mergeNotesTasksTableField(row, incomingData);
                        return applyTableFieldOverride(caseIdKey, merged);
                    }),
                );
            };

            const followupEventName = `work_queue_followup_date-${caseId}-${workqEventId}`;
            const followupHandler = (payload) => {
                if (isStaleSocketEvent(caseIdKey, payload)) {
                    return;
                }
                const rawIncoming = payload?.data ?? payload?.row ?? payload;
                const incomingTableField = rawIncoming?.work_queue_table_field;
                const hasTableField = incomingTableField && typeof incomingTableField === "object" && !Array.isArray(incomingTableField);
                const incomingData = hasTableField ? rawIncoming : publishReplacedNoteLists(rawIncoming);
                setRenderedRows((prevRows) =>
                    prevRows.map((row) => {
                        const rowCaseId = getCaseIdFromRow(row);
                        if (String(rowCaseId) !== caseIdKey) return row;
                        if (hasTableField) {
                            tableFieldOverridesRef.current[caseIdKey] = {
                                ...(tableFieldOverridesRef.current?.[caseIdKey] || {}),
                                ...incomingTableField,
                            };
                            return {
                                ...row,
                                work_queue_table_field: {
                                    ...(row?.work_queue_table_field || {}),
                                    ...incomingTableField,
                                },
                            };
                        }

                        const merged = mergeNotesTasksTableField(row, incomingData);
                        return applyTableFieldOverride(caseIdKey, merged);
                    }),
                );
            };

            updatedEventNames.forEach((eventName) => {
                socket.on(eventName, updatedHandler);
            });
            socket.on(notesEventName, notesHandler);
            socket.on(followupEventName, followupHandler);
            socketSubscriptionsRef.current[caseIdKey] = {
                updatedEventNames,
                updatedHandler,
                notesEventName,
                notesHandler,
                followupEventName,
                followupHandler,
            };
        }

        if (caseIdsToAdd.length === 0) {
            return;
        }

        const batchGen = ++socketSubscribeBatchGenRef.current;

        if (caseIdsToAdd.length <= WORKQ_SOCKET_SUBSCRIBE_SYNC_MAX) {
            caseIdsToAdd.forEach(subscribeWorkqCase);
            return () => {
                socketSubscribeBatchGenRef.current += 1;
            };
        }

        let sockIdx = 0;
        const pumpSock = () => {
            if (socketSubscribeBatchGenRef.current !== batchGen) return;
            const end = Math.min(sockIdx + WORKQ_SOCKET_SUBSCRIBE_CHUNK, caseIdsToAdd.length);
            for (; sockIdx < end; sockIdx += 1) {
                subscribeWorkqCase(caseIdsToAdd[sockIdx]);
            }
            if (sockIdx < caseIdsToAdd.length) {
                setTimeout(pumpSock, 0);
            }
        };
        setTimeout(pumpSock, 0);
        return () => {
            socketSubscribeBatchGenRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- renderedCaseIds tracked via displayedRowIdsSignature
    }, [displayedRowIdsSignature, workqEventId, isCustomWorkQ]);

    useEffect(() => {
        return () => {
            Object.values(socketSubscriptionsRef.current).forEach(
                ({ updatedEventName, updatedEventNames, updatedHandler, notesEventName, notesHandler, followupEventName, followupHandler }) => {
                const updateEvents = Array.isArray(updatedEventNames)
                    ? updatedEventNames
                    : (updatedEventName ? [updatedEventName] : []);
                updateEvents.forEach((eventName) => {
                    socket.off(eventName, updatedHandler);
                });
                socket.off(notesEventName, notesHandler);
                if (followupEventName && followupHandler) {
                    socket.off(followupEventName, followupHandler);
                }
            });
            socketSubscriptionsRef.current = {};
            activeWorkqEventIdRef.current = null;
            Object.values(notesAllSubsRef.current).forEach(({ eventName, handler }) => {
                socket.off(eventName, handler);
            });
            notesAllSubsRef.current = {};
        };
    }, []);

    useEffect(() => {
        if (isCustomWorkQ || !workqEventId || !emptyNoteTypes) {
            Object.keys(notesAllSubsRef.current).forEach((caseIdKey) => {
                const sub = notesAllSubsRef.current[caseIdKey];
                socket.off(sub.eventName, sub.handler);
                delete notesAllSubsRef.current[caseIdKey];
            });
            return;
        }

        const desiredCaseIds = new Set(renderedCaseIds.map((id) => String(id)));

        Object.keys(notesAllSubsRef.current).forEach((caseIdKey) => {
            if (!desiredCaseIds.has(caseIdKey)) {
                const sub = notesAllSubsRef.current[caseIdKey];
                socket.off(sub.eventName, sub.handler);
                delete notesAllSubsRef.current[caseIdKey];
            }
        });

        const notesIdsToAdd = renderedCaseIds.filter(
            (caseId) => !notesAllSubsRef.current[String(caseId)],
        );
        const WORKQ_NOTES_ALL_SYNC_MAX = 120;
        const WORKQ_NOTES_ALL_CHUNK = 22;

        function subscribeNotesAll(caseId) {
            const caseIdKey = String(caseId);
            if (notesAllSubsRef.current[caseIdKey]) return;

            const eventName = `work_queue_notes_all_${caseId}`;

            const handler = (payload) => {
                const incomingData = payload?.data ?? payload?.row ?? payload;
                const hasNotes = Array.isArray(incomingData?.notes);
                const notes = hasNotes ? omitDeletedRecords(incomingData.notes) : incomingData?.notes;
                if (hasNotes) {
                    dispatch(filterActions.batchUpdate({ caseId, data: { notes } }));
                    dispatchDeletedTasksRemoved(dispatch, incomingData.notes);
                }

                setRenderedRows((prevRows) =>
                    prevRows.map((row) => {
                        const rowCaseId = String(getCaseIdFromRow(row));
                        if (rowCaseId !== caseIdKey) return row;
                        return replaceNotesIfNoNoteTypes(row, hasNotes ? { ...incomingData, notes } : incomingData);
                    }),
                );
            };

            socket.on(eventName, handler);
            notesAllSubsRef.current[caseIdKey] = { eventName, handler };
        };

        if (notesIdsToAdd.length === 0) {
            return;
        }

        if (notesIdsToAdd.length <= WORKQ_NOTES_ALL_SYNC_MAX) {
            notesIdsToAdd.forEach(subscribeNotesAll);
            return () => {
                notesAllSubscribeBatchGenRef.current += 1;
            };
        }

        const notesGen = ++notesAllSubscribeBatchGenRef.current;
        let nIdx = 0;
        const pumpNotes = () => {
            if (notesAllSubscribeBatchGenRef.current !== notesGen) return;
            const end = Math.min(nIdx + WORKQ_NOTES_ALL_CHUNK, notesIdsToAdd.length);
            for (; nIdx < end; nIdx += 1) {
                subscribeNotesAll(notesIdsToAdd[nIdx]);
            }
            if (nIdx < notesIdsToAdd.length) {
                setTimeout(pumpNotes, 0);
            }
        };
        setTimeout(pumpNotes, 0);
        return () => {
            notesAllSubscribeBatchGenRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- renderedCaseIds tracked via displayedRowIdsSignature
    }, [isCustomWorkQ, workqEventId, emptyNoteTypes, displayedRowIdsSignature]);

    // Memoize the table rows to prevent re-rendering when other props change
    const tableRows = useMemo(() => {
        const rows = (displayedRows || []).filter((row) => row?.case?.id);
        if (isCustomWorkQ || !rows.length) return null;

        if (!usesVirtualTableBody) {
            return rows.map((item, index) => (
                <WorkQRow
                    key={item.case.id}
                    data={item}
                    index={index}
                    slug={workqSlug}
                    workq={workq}
                    groupedFields={normalizedGroupedFields}
                    deferRowPaint={false}
                />
            ));
        }

        const { start: vStart, end: vEnd, padTop: topPadPx, padBot: botPadPx } = virtualWindow;
        const slice = displayedRows.slice(vStart, vEnd).filter((item) => item?.case?.id);
        const padTopTr =
            topPadPx > 0 ? (
                <tr key="__wq-vpad-top" aria-hidden="true">
                    <td
                        colSpan={virtualTableColCount}
                        style={{ padding: 0, border: "none", height: topPadPx, lineHeight: 0 }}
                    />
                </tr>
            ) : null;
        const padBotTr =
            botPadPx > 0 ? (
                <tr key="__wq-vpad-bot" aria-hidden="true">
                    <td
                        colSpan={virtualTableColCount}
                        style={{ padding: 0, border: "none", height: botPadPx, lineHeight: 0 }}
                    />
                </tr>
            ) : null;

        return (
            <>
                {padTopTr}
                {slice.map((item, i) => (
                    <WorkQRow
                        key={item.case.id}
                        data={item}
                        index={vStart + i}
                        slug={workqSlug}
                        workq={workq}
                        groupedFields={normalizedGroupedFields}
                        deferRowPaint={true}
                    />
                ))}
                {padBotTr}
            </>
        );
    }, [
        isCustomWorkQ,
        displayedRows,
        usesVirtualTableBody,
        virtualWindow,
        virtualTableColCount,
        workqSlug,
        workq,
        normalizedGroupedFields,
    ]);

    const shouldBlockInteractionsForLoading = showTableLoadingOverlay;
    
    // Memoize the custom workqs component
    const customWorkQs = useMemo(() => {
        if (!workq || workq.type !== 'custom') return null;
        
        return <CustomWorkQs slug={defaultSlug} />;
    }, [workq, defaultSlug]);
    
    // Memoize the normal workqs content
    const normalWorkQsInner = useMemo(() => {
        if (!workq || workq.type === 'custom') return null;
        
        return (
            <div className={classes.normal_container}>
                <OverflowContainer
                    prevent={false}
                    containerRef={workqScrollRef}
                    onScroll={onWorkqTableScroll}
                    className={classes.workq_table_scroll}
                >
                    <WorkQComponentLoading classes={classes}/>
                    <div className={classes.table_wrapper}>
                        {showTableLoadingOverlay && <div className={classes.table_loading_overlay} style={{ pointerEvents: "all" }}>
                            <div className={classes.table_loading_overlay_inner}>
                                <span className={`loader`} />
                                <div>Loading…</div>
                            </div>
                        </div>}

                        <table
                            ref={tableStickyCssRef}
                            className={`${classes.table} table-border-radius-8 ${shouldBlockInteractionsForLoading ? classes.table_container_loading : ''}`}
                        >
                        {patientsList && (
                            <thead className={classes.table_header}>
                                <WorkQHeader slug={workqSlug}/>
                            </thead>
                        )}
                        {patientsList?.data && (
                            <tbody>
                                {tableRows}
                            </tbody>
                        )}          
                        </table>
                    </div>
                </OverflowContainer>
                
                {patientsList?.meta && (
                    <div className={classes.pagination_container}>
                        <MemoizedPagination 
                            data={patientsList} 
                            apiType={defaultSlug} 
                        />
                    </div>
                )}
            </div>
        );
    }, [
        workq,
        patientsList,
        workqSlug,
        tableRows,
        defaultSlug,
        showTableLoadingOverlay,
        shouldBlockInteractionsForLoading,
        onWorkqTableScroll,
    ]);


    return (
        <>
            <div className={classes.main_container}>
                <WorkQShellProvider>
                    {customWorkQs || normalWorkQsInner}
                </WorkQShellProvider>
            </div>
            {/* {flyoutControls} */}
        </>
    );
}